/**
 * Case Notes (#692) — LegalFlare-internal, operator-authored notes on a matter.
 *
 * WHY THIS EXISTS. Command Center's event vocabulary is closed: it records the
 * transitions the system itself causes (Sent, Reviewing, Accepted, Placed). A
 * thing that happens *to* a matter without the system causing it — a handling
 * firm's excuse on a call, an originating firm going quiet, a client changing
 * their mind, an operator deciding to hold rather than reroute — has no event
 * type and therefore cannot be written. It is not dropped; it is
 * unrepresentable. Since pt 68 the posture is "the system detects and surfaces;
 * humans decide and act" — the deciding moved into people, and no place was
 * created for a person to write down what they decided. A Case Note is the
 * open-vocabulary event that closes that gap.
 *
 * NEVER firm-visible. No portal surface reads this table; the notes exist so LF
 * can reason about its own matters and, in aggregate, about the firms it works
 * with.
 *
 * APPEND-ONLY. There is no edit and no delete verb. A correction is a new note.
 * This mirrors the Outreach Log's documented posture and keeps the case
 * timeline honest — a record you can rewrite is a document, not a history.
 */

import { z } from "zod";

/**
 * What a note is *about* — declared by the author at write time, NEVER inferred
 * from where the note sits or from its text.
 *
 * The values are a routing question, not a taxonomy of subject matter: which
 * firm's record does this reach?
 *
 *   - `hf`     — the handling firm. Reaches that firm's Firm Success page.
 *   - `of`     — the originating firm. Reaches that firm's Firm Success page.
 *   - `client` — the client. Reaches no firm record.
 *   - `lf`     — LegalFlare's own reasoning about the matter (held rather than
 *                rerouted, and why). Reaches no firm record.
 *
 * `client` and `lf` both answer "no firm" and are indistinguishable for
 * routing; they stay distinct because filing our own reasoning under a party
 * would put it inside that party's record, where it later reads as a fact about
 * them.
 *
 * SINGLE-VALUED, by decision. A note concerning two parties is written under
 * what it primarily concerns, with the other named in the body. The accepted
 * cost: the second firm's page will not surface it. Multi-valued `about` would
 * make the storage rule below ambiguous — one note would be both Placement- and
 * Lead-shaped — for a case we have not seen demand for.
 */
export const CASE_NOTE_ABOUTS = ["hf", "of", "client", "lf"] as const;
export const CaseNoteAboutSchema = z.enum(CASE_NOTE_ABOUTS);
export type CaseNoteAbout = z.infer<typeof CaseNoteAboutSchema>;

/**
 * Where a note is stored, derived from what it is about. The single source of
 * this rule — the adapter, the routes and the tests all call it rather than
 * re-expressing it.
 *
 * A note about the handling firm anchors to the **Placement**, because a call
 * about a matter concerns that matter×firm *pairing*. If the matter reroutes,
 * that call belongs to the placement that died, not to its replacement — which
 * is also why a declined placement keeps its notes.
 *
 * Everything else anchors to the **Lead**: client acts, originating-firm acts,
 * and our own decisions all concern the matter itself, independent of whichever
 * handling firm happens to hold it.
 */
export function anchorFor(about: CaseNoteAbout): "placement" | "lead" {
  return about === "hf" ? "placement" : "lead";
}

/** A Case Note as every surface consumes it. */
export const CaseNoteSchema = z.object({
  /** Airtable record ID. */
  id: z.string(),
  /**
   * ISO-8601 datetime — Airtable's system `createdTime`. When the note was
   * WRITTEN DOWN, which is not when the thing happened (see `occurredOn`).
   */
  recordedAt: z.string(),
  /**
   * YYYY-MM-DD — when the thing actually HAPPENED. Operator-set, defaulting to
   * today. Writing work up days later is the normal case, not the exception,
   * and a single clock would silently falsify the history every time it did.
   */
  occurredOn: z.string(),
  /** Authoring LF operator (email). Stamped server-side. */
  author: z.string(),
  /** The note itself. */
  body: z.string(),
  about: CaseNoteAboutSchema,
  /** Placement record id — non-null iff `about === "hf"`. */
  placementRecordId: z.string().nullable(),
  /** Lead record id — non-null iff `about !== "hf"`. */
  leadRecordId: z.string().nullable(),
});
export type CaseNote = z.infer<typeof CaseNoteSchema>;

/**
 * The write body for a note on a placement. `about` is not a parameter here —
 * the route is placement-scoped, so the note is necessarily about the handling
 * firm. The lead-scoped route (wave 3) carries `about` explicitly because there
 * the value is a genuine choice.
 */
export const AddCaseNoteRequestSchema = z.object({
  /** Required, non-empty after trim — an empty note records nothing. */
  body: z.string().trim().min(1).max(10_000),
  /**
   * YYYY-MM-DD. Omitted means today. Accepts a past date so work can be written
   * up after the fact; a FUTURE date is rejected — a note records what
   * happened, not what is planned (that is what a Task is for).
   */
  occurredOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "occurredOn must be YYYY-MM-DD")
    .optional(),
  /**
   * Optional retry guard. A repeat with the same key returns the existing note
   * instead of creating a second one. It matters more here than it would
   * elsewhere: notes are append-only, so a duplicate cannot be cleaned up.
   */
  idempotencyKey: z.string().min(1).max(255).optional(),
});
export type AddCaseNoteRequest = z.infer<typeof AddCaseNoteRequestSchema>;

/**
 * The write body for a note on a LEAD — about the originating firm, the client,
 * or LegalFlare's own reasoning.
 *
 * `about` IS a parameter here, unlike the placement-scoped write. That is the
 * asymmetry the design intends: a note on a placement is necessarily about the
 * handling firm, so asking would be theatre; a note on a matter genuinely could
 * concern any of three parties, and which one decides whose record it reaches.
 * `hf` is excluded because it would anchor to a placement, not this lead.
 */
export const AddLeadCaseNoteRequestSchema = AddCaseNoteRequestSchema.extend({
  about: z.enum(["of", "client", "lf"]),
});
export type AddLeadCaseNoteRequest = z.infer<typeof AddLeadCaseNoteRequestSchema>;

/** The list envelope the placement-scoped route returns. */
export const CaseNotesResponseSchema = z.object({
  notes: z.array(CaseNoteSchema),
});
export type CaseNotesResponse = z.infer<typeof CaseNotesResponseSchema>;

/**
 * One place a note can be written on this matter. Returned alongside the
 * matter's notes because the panel otherwise has no way to know: its routing
 * legs carry a firm NAME but no placement record id, and a note about a
 * handling firm must anchor to a specific placement.
 *
 * Named `targets` after the messages route's compose-targets read, which
 * answers the same question for a different write.
 */
export const CaseNoteTargetSchema = z.object({
  placementId: z.string(),
  firmName: z.string().nullable(),
  /** The placement's current decision — lets the composer show which firms are
   *  live and which are history without a second lookup. */
  decision: z.string().nullable(),
});
export type CaseNoteTarget = z.infer<typeof CaseNoteTargetSchema>;

/**
 * The matter-scoped envelope: every note on the case — lead-anchored plus every
 * placement's, including placements that were declined or pulled — and the
 * placements a new note can be written against.
 */
export const CaseNotesForLeadResponseSchema = z.object({
  notes: z.array(CaseNoteSchema),
  targets: z.array(CaseNoteTargetSchema),
});
export type CaseNotesForLeadResponse = z.infer<typeof CaseNotesForLeadResponseSchema>;

/**
 * A firm's notes, split by the capacity they concern (#692 wave 2).
 *
 * `hf` — notes on the placements this firm has HANDLED, including ones it
 * declined or was pulled from. A firm's record should not quietly drop the
 * matters it turned down; how a firm behaves on the cases it does not take is
 * part of what LF is trying to see.
 *
 * `of` — notes on the matters this firm ORIGINATED that are about it. Filtered
 * to `about === "of"` at the source: the client and LF-reasoning notes sitting
 * on those same leads are not facts about the sending firm, and letting them
 * through would fill a firm's record with observations about somebody else.
 *
 * Both newest-first. A case is read as a story from the beginning; a firm is
 * read as "what has been going on lately".
 */
export const FirmCaseNotesResponseSchema = z.object({
  hf: z.array(CaseNoteSchema),
  of: z.array(CaseNoteSchema),
});
export type FirmCaseNotesResponse = z.infer<typeof FirmCaseNotesResponseSchema>;
